import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { messageApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  EntityCell,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  useToast,
} from '@/components/ui';
import { Composer } from '@/features/messages/Composer';
import { ConversationList } from '@/features/messages/ConversationList';
import { Thread } from '@/features/messages/Thread';
import { useIsTyping, useRealtime } from '@/features/realtime/useRealtime';
import { conversationSubject } from '@/features/messages/subject';
import { errorMessage } from '@/hooks/useApiError';

/**
 * The inbox, shared by both shells.
 *
 * One component rather than a student copy and a company copy, because it is
 * genuinely one screen: the API resolves which side the caller is on, so there
 * is nothing here that differs between them. Two copies would be two places
 * for the wording to drift.
 *
 * The socket is not visible in this file beyond a connection indicator. A
 * frame invalidates a query and this screen re-renders from the refetch, which
 * is why a dropped connection costs latency and nothing else — the slow poll
 * below is the safety net.
 */
export function MessagesPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('c');
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const { connected } = useRealtime();
  const isTyping = useIsTyping(selectedId);

  const conversations = useQuery({
    queryKey: ['conversations'],
    queryFn: () => messageApi.listConversations({ page: 1, page_size: 50 }),
    // The socket is the fast path; this is what makes a failed socket a
    // slower product rather than a broken one.
    refetchInterval: 60_000,
  });

  const thread = useQuery({
    queryKey: ['conversation', selectedId],
    queryFn: () => messageApi.getConversation(selectedId, { page: 1, page_size: 100 }),
    enabled: Boolean(selectedId),
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
    queryClient.invalidateQueries({ queryKey: ['messages-unread'] });
    if (selectedId) {
      queryClient.invalidateQueries({ queryKey: ['conversation', selectedId] });
    }
  }

  // Opening a thread marks it read for your side. Doing it here rather than on
  // click means it also happens when a socket frame brings a new message into
  // a thread already on screen.
  useEffect(() => {
    if (!selectedId || !thread.data) return;
    if (thread.data.unread_count === 0) return;
    messageApi
      .markConversationRead(selectedId)
      .then(refresh)
      .catch(() => {
        // Not worth interrupting anybody over; the badge corrects itself.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, thread.data?.unread_count]);

  const send = useMutation({
    mutationFn: (body) => messageApi.sendMessage(selectedId, body),
    onSuccess: refresh,
    onError: (error) => toast.error(errorMessage(error)),
  });

  const attach = useMutation({
    mutationFn: ({ file, caption }) => messageApi.sendAttachment(selectedId, file, caption),
    onSuccess: refresh,
    onError: (error) => toast.error(errorMessage(error)),
  });

  const report = useMutation({
    mutationFn: () => messageApi.reportConversation(selectedId, reason),
    onSuccess: () => {
      setReporting(false);
      setReason('');
      toast.success('Reported. Somebody will look at this.');
      refresh();
    },
    onError: (error) => {
      setReporting(false);
      toast.error(errorMessage(error));
    },
  });

  async function download(attachment) {
    setDownloadingId(attachment.id);
    try {
      const link = await messageApi.getAttachmentLink(selectedId, attachment.id);
      window.open(resolveFileUrl(link.url), '_blank', 'noopener');
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setDownloadingId(null);
    }
  }

  if (conversations.isPending) return <LoadingState label="Loading your messages…" />;
  if (conversations.isError) {
    return (
      <ErrorState
        message={errorMessage(conversations.error)}
        onRetry={() => void conversations.refetch()}
      />
    );
  }

  const rows = conversations.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Messages"
        description="Conversations about your applications, and questions about internships."
      />

      <div className="grid items-start gap-6 lg:grid-cols-[340px_1fr]">
        <Card className="overflow-hidden p-0 sm:p-0">
          <div className="border-line flex items-center justify-between gap-3 border-b px-4 py-3">
            <h2 className="text-ink flex items-center gap-2 text-sm font-semibold">
              <Icon name="messages" size={16} className="text-brand-600" />
              Conversations
              <span className="text-ink-muted font-normal tabular-nums">{rows.length}</span>
            </h2>
            <Badge tone={connected ? 'success' : 'neutral'} dot>
              {connected ? 'Live' : 'Reconnecting'}
            </Badge>
          </div>
          <ConversationList
            conversations={rows}
            selectedId={selectedId}
            onSelect={(id) => setParams({ c: id })}
          />
        </Card>

        <Card className="flex min-h-112 flex-col">
          {!selectedId ? (
            <EmptyState
              icon={<Icon name="messages" size={22} />}
              title="Nothing selected"
              description="Choose a conversation to read it."
            />
          ) : thread.isPending ? (
            <LoadingState label="Loading conversation…" />
          ) : thread.isError ? (
            <ErrorState
              message={errorMessage(thread.error)}
              onRetry={() => void thread.refetch()}
            />
          ) : (
            <>
              <header className="border-line -mx-5 -mt-5 mb-4 flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4 sm:-mx-6 sm:-mt-6 sm:px-6">
                <EntityCell
                  name={thread.data.counterpart_name}
                  subtitle={conversationSubject(thread.data)}
                />
                {thread.data.is_reported ? (
                  <Badge tone="warning" dot>
                    Reported
                  </Badge>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => setReporting(true)}>
                    <Icon name="alert" size={15} />
                    Report
                  </Button>
                )}
              </header>

              <div className="max-h-104 flex-1 overflow-y-auto pr-1 pb-2">
                <Thread
                  conversation={thread.data}
                  messages={thread.data.messages}
                  isTyping={isTyping}
                  onDownload={download}
                  downloadingId={downloadingId}
                />
              </div>

              <Composer
                conversationId={selectedId}
                isSending={send.isPending || attach.isPending}
                onSend={(body) => send.mutateAsync(body)}
                onAttach={(file, caption) => attach.mutateAsync({ file, caption })}
              />
            </>
          )}
        </Card>
      </div>

      {/* A Modal rather than a ConfirmDialog: reporting asks for a reason, and
          a confirmation dialog is for questions that only need a yes. */}
      <Modal
        open={reporting}
        title="Report this conversation?"
        description="A Super Admin will be able to read it. Until somebody reports a conversation, nobody at Join-Wonder can open it."
        onClose={() => setReporting(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setReporting(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              isLoading={report.isPending}
              disabled={reason.trim().length < 3}
              onClick={() => report.mutate()}
            >
              Report
            </Button>
          </>
        }
      >
        <Input
          label="What is wrong?"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Tell us what happened"
        />
      </Modal>
    </div>
  );
}
